import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import RichTextEditor from '../../components/editor/RichTextEditor';
import { esHtmlVacio, sanitizarHtml } from '../../components/editor/sanitizarHtml';
import PermisoDenegado from '../../components/PermisoDenegado';
import Loader from '../../components/Loader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL, { IMAGE_DOMAIN } from '../../config/config';
import { cabecerasAuth, claves } from '../../queries/cliente';
import Verificacion from '../../components/Verificacion';
import Suspendido from '../../components/Suspendido';
import TicketSelect from './TicketSelect';
import MensajeChat from './ticket/MensajeChat';
import FilePreviewModal from '../../components/files/FilePreviewModal';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { getFileName, getIconUrl } from './ticket/archivos';
import { formatearFechaLimite, desdeIso, hoy } from '../../utils/fechaLimite';
import { contieneTermino, htmlATexto } from './ticket/busqueda';
import { textoParaCopiar } from './ticket/texto';
import { construirCopiaTicket } from './ticket/copia';
import {
    FiArrowLeft, FiUserCheck, FiUserX, FiSearch, FiChevronDown, FiX,
    FiSend, FiDownload, FiCopy, FiUser, FiMail, FiPhone, FiClock,
    FiGlobe, FiHash, FiArrowDown, FiChevronUp, FiCornerUpLeft,
    FiZap, FiAlertCircle, FiFolder, FiSettings,
    FiInfo, FiLock, FiCheck, FiFileText
} from 'react-icons/fi';

// Los value/badgeClass son estables; el label se resuelve con t() al usarlos
const CLAVES_DPTO = ['no_especificado', 'desarrollo', 'diseno', 'marketing', 'redes_sociales'];
const CLAVES_PRIORIDAD = ['no_urgente', 'urgente'];
const CLAVES_ESTADO = ['abierto', 'retenido', 'cerrado'];

const Ticket = () => {
    const { identificador } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const opcionesDpto = CLAVES_DPTO.map((value) => ({
        value, label: t(`tickets.departamentos.${value}`), badgeClass: value.replace('_', '-'),
    }));
    const opcionesPrioridad = CLAVES_PRIORIDAD.map((value) => ({
        value, label: t(`tickets.prioridades.${value}`), badgeClass: value === 'urgente' ? 'alta' : 'sin-prioridad',
    }));
    const opcionesEstado = CLAVES_ESTADO.map((value) => ({
        value, label: t(`tickets.estados.${value}`), badgeClass: value,
    }));
    const token = localStorage.getItem('AuthToken');
    const tokenInfo = token ? jwtDecode(token) : {};
    const queryClient = useQueryClient();

    const consultaTicket = useQuery({
        queryKey: claves.ticket(identificador),
        queryFn: async () => {
            const response = await axios.get(`${BASE_URL}/obtener-ticket/${identificador}`, cabecerasAuth());
            const datos = response.data.ticket;
            if (!datos) return { chat: [] };
            // El chat llega como JSON serializado según el endpoint
            datos.chat = Array.isArray(datos.chat) ? datos.chat : JSON.parse(datos.chat);
            return datos;
        },
        enabled: Boolean(identificador),
        // La conversación se mantiene viva por socket: no hace falta revalidar
        // al volver a la pestaña y pisar lo que ya han traído los eventos.
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const ticket = consultaTicket.data || { chat: [] };

    /**
     * Escribe en la caché del ticket. Mantiene la firma de un setState (acepta
     * objeto o función) para que los handlers de socket sigan igual.
     */
    const setTicket = useCallback((actualizador) => {
        queryClient.setQueryData(claves.ticket(identificador), (previo) => {
            const base = previo || { chat: [] };
            return typeof actualizador === 'function' ? actualizador(base) : actualizador;
        });
    }, [identificador, queryClient]);
    const [content, setContent] = useState('');
    const [socket, setSocket] = useState(null);

    const [usuarioEscribiendo, setUsuarioEscribiendo] = useState(null);

    const [isSending, setIsSending] = useState(false); // Estado para controlar "Enviando..."

    const [selectedFiles, setSelectedFiles] = useState([]); // Estado para los archivos seleccionados
    const [filesToUpload, setFilesToUpload] = useState([]); // Archivos para enviar al backend
    const [showAllFiles, setShowAllFiles] = useState(false);
    const [errorAlEnviar, setErrorAlEnviar] = useState(false);
    const [animatingMessages, setAnimatingMessages] = useState([]); // Estado para animaciones de mensajes
    const [mensajeEnEdicion, setMensajeEnEdicion] = useState(null); // id del mensaje que se está editando
    const [mensajeDestacado, setMensajeDestacado] = useState(null); // id resaltado por enlace directo o búsqueda
    const [notaInterna, setNotaInterna] = useState(false); // el próximo mensaje es una nota interna
    const [respondiendoA, setRespondiendoA] = useState(null); // mensaje al que responde el composer
    const [busqueda, setBusqueda] = useState('');
    const [busquedaAbierta, setBusquedaAbierta] = useState(false);
    const [indiceCoincidencia, setIndiceCoincidencia] = useState(0);
    const [idsNoLeidos, setIdsNoLeidos] = useState([]); // congelado al entrar: marca la divisoria
    const [lejosDelFinal, setLejosDelFinal] = useState(false);
    const [mensajesSinVer, setMensajesSinVer] = useState(0);
    const [exportandoPdf, setExportandoPdf] = useState(false);
    const [datosDrawerAbierto, setDatosDrawerAbierto] = useState(false); // Drawer de datos del ticket - por defecto cerrado (chat full-width)
    const [showTecnicoSelector, setShowTecnicoSelector] = useState(false); // Estado para mostrar selector de técnico
    const [searchTecnico, setSearchTecnico] = useState(''); // Estado para búsqueda de técnico
    const [filePreview, setFilePreview] = useState(null); // Estado para preview de archivo (url, tipo)

    const chatTicketRef = useRef(null);
    const tecnicoSelectorRef = useRef(null);

    const processFiles = (files) => {
        // Punto único de entrada de adjuntos (arrastre sobre el chat, editor y pegado)
        const maxFileSize = 5 * 1024 * 1024; // 5 MB
        const validFiles = [];
        const invalidFiles = [];

        files.forEach((file) => {
            if (file.size <= maxFileSize) {
                validFiles.push(file);
            } else {
                invalidFiles.push({ name: file.name, size: (file.size / (1024 * 1024)).toFixed(2) });
            }
        });

        if (invalidFiles.length > 0) {
            const errorMessage = invalidFiles
                .map((file) => `${file.name} (${file.size} MB)`)
                .join('\n');
            toast.error(
                t('ticket.archivosExceden', { lista: errorMessage }),
                { position: "bottom-right", autoClose: 7000 }
            );
        }

        setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
        setFilesToUpload((prevFiles) => [...prevFiles, ...validFiles]);

    };

    const handleRemoveFile = (index) => {
        setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
        setFilesToUpload((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const chatRef = useRef(null);
    const bottomRef = useRef(null);
    // Se consulta desde los listeners del socket, donde el estado llegaría obsoleto
    const lejosDelFinalRef = useRef(false);
    const noLeidosCalculadosRef = useRef(false);

    const processQuotes = (texto) => {
        const escapeHtml = (text) => {
            return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        };
        return texto.replace(/"([^"]*)"/g, (match, p1) => `"${escapeHtml(p1)}"`);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tecnicoSelectorRef.current && !tecnicoSelectorRef.current.contains(event.target)) {
                setShowTecnicoSelector(false);
            }
        };

        if (showTecnicoSelector) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showTecnicoSelector]);

    /**
     * Datos del ticket abierto: en móvil ocupa toda la pantalla, así que el fondo
     * no debe seguir haciendo scroll detrás, y Escape tiene que cerrarlo.
     */
    useEffect(() => {
        if (!datosDrawerAbierto) return undefined;

        const alPulsarTecla = (evento) => {
            if (evento.key === 'Escape') setDatosDrawerAbierto(false);
        };

        const overflowPrevio = document.body.style.overflow;
        const esPantallaEstrecha = window.matchMedia('(max-width: 1100px)').matches;
        if (esPantallaEstrecha) document.body.style.overflow = 'hidden';

        document.addEventListener('keydown', alPulsarTecla);

        return () => {
            document.removeEventListener('keydown', alPulsarTecla);
            document.body.style.overflow = overflowPrevio;
        };
    }, [datosDrawerAbierto]);


    // Autor del ticket y, si lo abrió un técnico en su nombre, quién lo abrió.
    // Dependen del ticket, así que no arrancan hasta tenerlo.
    const abiertoPorOtro = Boolean(ticket.abierto_por && ticket.abierto_por !== ticket.usuario_creador);

    const [consultaCreador, consultaAbridor, consultaTecnicos] = useQueries({
        queries: [
            {
                queryKey: claves.usuario(ticket.usuario_creador),
                queryFn: async () => (await axios.get(`${BASE_URL}/usuario/info/${ticket.usuario_creador}`, cabecerasAuth())).data.usuario,
                enabled: Boolean(ticket.usuario_creador),
            },
            {
                queryKey: claves.usuario(ticket.abierto_por),
                queryFn: async () => (await axios.get(`${BASE_URL}/usuario/info/${ticket.abierto_por}`, cabecerasAuth())).data.usuario,
                enabled: abiertoPorOtro,
            },
            {
                queryKey: claves.tecnicos(),
                queryFn: async () => (await axios.get(`${BASE_URL}/usuario/tecnicos`, cabecerasAuth())).data.usuarios || [],
                staleTime: 5 * 60_000,
            },
        ],
    });

    const usuarioCreador = consultaCreador.data || null;
    const usuarioAbridor = abiertoPorOtro ? (consultaAbridor.data || null) : null;
    const tecnicos = consultaTecnicos.data || [];

    // Vencida solo mientras el ticket sigue vivo: en uno cerrado ya no aporta
    const fechaLimiteVencida = useMemo(() => {
        if (!ticket.fecha_limite || ticket.estado_del_ticket === 'cerrado') return false;
        const limite = desdeIso(ticket.fecha_limite);
        return Boolean(limite) && limite < hoy();
    }, [ticket.fecha_limite, ticket.estado_del_ticket]);

    const permisosVerificados = !consultaTicket.isPending;
    // Quien decide es el servidor: si no puedes verlo, responde 403 y ni siquiera
    // llegan los datos. Antes esto se volvía a calcular aquí comparando el creador
    // con el usuario en sesión, y esa copia se quedó atrás en cuanto un
    // responsable pudo ver los tickets de su equipo: el servidor los servía y la
    // pantalla los bloqueaba igualmente.
    const permisoDenegado = [401, 403].includes(consultaTicket.error?.response?.status);

    // Mover mensajesConDescripcion aquí para evitar error de inicialización
    const mensajesConDescripcion = useMemo(() => {
        if (!ticket) return [];

        // 1) Mensaje "sintético" con la descripción del ticket
        // Si el ticket fue abierto por un técnico/admin en nombre de otro usuario,
        // el primer mensaje aparece firmado por el abridor, no por el usuario_creador
        const autor = usuarioAbridor || usuarioCreador;
        const primerMensaje = {
            usuario: autor?.usuario || ticket.usuario_creador || 'desconocido',
            nombre: autor?.nombre || ticket.nombre || '',
            apellidos: autor?.apellidos || ticket.apellidos || '',
            fotoPerfil: autor?.fotoPerfil || '/images/usuario.png',
            role: autor?.role || 'user',
            texto: ticket.descripcion || '',
            fecha: ticket.createdAt || new Date().toISOString(),
            archivos: ticket.archivos ? ticket.archivos.split(',') : [],
            __esDescripcion: true,
        };

        // 2) Resto de mensajes del chat ordenados por fecha ascendente
        const chatOrdenado = (Array.isArray(ticket.chat) ? ticket.chat : []).slice().sort((a, b) => {
            const fa = new Date(a.fecha).getTime();
            const fb = new Date(b.fecha).getTime();
            return fa - fb;
        });

        return [primerMensaje, ...chatOrdenado];
    }, [ticket, usuarioCreador, usuarioAbridor]);

    // Todos los archivos del ticket: los de creación + los adjuntados en cualquier mensaje del chat
    const todosLosArchivos = useMemo(() => {
        return mensajesConDescripcion.reduce((acc, msg) => {
            if (Array.isArray(msg.archivos) && msg.archivos.length > 0) {
                acc.push(...msg.archivos);
            }
            return acc;
        }, []);
    }, [mensajesConDescripcion]);

    const scrollToBottom = (instant = false) => {
        if (chatRef.current) {
            if (instant) {
                // Con column-reverse, scroll a 0 es el final
                chatRef.current.scrollTop = 0;
            } else {
                // Scroll suave al final (que es scrollTop = 0 con column-reverse)
                chatRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        }
    };

    const scrollToTop = () => {
        if (chatRef.current) {
            chatRef.current.scrollTop = 0;
        }
    };

    // Con column-reverse, el scroll ya comienza al final naturalmente
    // Solo necesitamos scroll suave cuando llegan nuevos mensajes
    useEffect(() => {
        if (ticket.chat.length > 0 && chatRef.current) {
            // Scroll suave cuando llegan mensajes nuevos
            const timeout = setTimeout(() => {
                scrollToBottom(false);
            }, 200);
            return () => clearTimeout(timeout);
        }
    }, [ticket.chat.length]);

    const handleChange = (value) => {
        setContent(value);
        if (errorAlEnviar && !esHtmlVacio(value)) setErrorAlEnviar(false);
        if (socket) {
            socket.emit('escribiendo', {
                identificador: identificador,
                usuario: tokenInfo.usuario,
                nombre: tokenInfo.nombre,
                apellidos: tokenInfo.apellidos,
            });
        }
    };

    const copiarTicket = async () => {
        const etiqueta = (opciones, valor) => opciones.find((o) => o.value === valor)?.label || '';
        const { texto, html } = construirCopiaTicket({
            ticket,
            etiquetas: {
                estado: etiqueta(opcionesEstado, ticket.estado_del_ticket),
                prioridad: etiqueta(opcionesPrioridad, ticket.prioridad),
                departamento: etiqueta(opcionesDpto, ticket.dpto),
                tecnico: obtenerNombreTecnicoAsignado() || t('ticket.sinAsignar'),
                fechaLimite: formatearFechaLimite(ticket.fecha_limite),
                urlSolicitud: ticket.url_solicitud || t('ticket.urlNoAplica'),
            },
            archivos: todosLosArchivos,
            enlace: window.location.href,
        });

        try {
            if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([texto], { type: 'text/plain' }),
                    }),
                ]);
            } else {
                // Safari antiguo y contextos sin ClipboardItem: al menos el texto
                await navigator.clipboard.writeText(texto);
            }
            toast.success(t('ticket.infoCopiada'), { position: 'bottom-right' });
        } catch (err) {
            console.error('Error copiando al portapapeles:', err);
            toast.error(t('ticket.errorCopiarInfo'), { position: 'bottom-right' });
        }
    };

    /**
     * El PDF lo compone el servidor (pdf-lib): así el documento sale igual sea
     * cual sea el navegador y no hay que arrastrar una librería de PDF al
     * bundle del cliente. La descarga va por axios para poder mandar el token.
     */
    const exportarTicketPdf = async () => {
        try {
            setExportandoPdf(true);
            const respuesta = await axios.get(`${BASE_URL}/ticket-pdf/${encodeURIComponent(identificador)}`, {
                ...cabecerasAuth(),
                params: { idioma: i18n.language?.startsWith('en') ? 'en' : 'es', enlace: window.location.href },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(respuesta.data);
            const enlace = document.createElement('a');
            enlace.href = url;
            enlace.download = `ticket-${identificador}.pdf`;
            document.body.appendChild(enlace);
            enlace.click();
            document.body.removeChild(enlace);
            window.URL.revokeObjectURL(url);
            toast.success(t('ticket.pdfDescargado'), { position: 'bottom-right' });
        } catch (error) {
            console.error('Error al exportar el ticket a PDF:', error);
            toast.error(t('ticket.errorExportarPdf'), { position: 'bottom-right' });
        } finally {
            setExportandoPdf(false);
        }
    };

    const descargarArchivo = async (url) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = url.split('/').pop() || 'archivo';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error al descargar el archivo:', error);
            toast.error(t('ticket.errorDescargar'), { position: "bottom-right" });
        }
    };



    const actualizarDpto = async (nuevoDpto) => {
        try {
            await axios.put(`${BASE_URL}/actualizar-dpto`, { identificador, dpto: nuevoDpto }, {
                headers: { Authorization: token }
            });
            setTicket((prevTicket) => ({
                ...prevTicket,
                dpto: nuevoDpto,
            }));
        } catch (error) {
            console.error('Error al actualizar el dpto:', error);
        }
    };

    const actualizarPrioridad = async (nuevaPrioridad) => {
        try {
            await axios.put(`${BASE_URL}/actualizar-prioridad`, { identificador, prioridad: nuevaPrioridad }, {
                headers: { Authorization: token }
            });
            setTicket((prevTicket) => ({
                ...prevTicket,
                prioridad: nuevaPrioridad,
            }));
        } catch (error) {
            console.error('Error al actualizar la prioridad:', error);
        }
    };

    const actualizarEstadoDelTicket = async (nuevoEstado) => {
        try {
            await axios.put(`${BASE_URL}/actualizar-estado_del_ticket`, { identificador, estado_del_ticket: nuevoEstado }, {
                headers: { Authorization: token }
            });
            setTicket((prevTicket) => ({
                ...prevTicket,
                estado_del_ticket: nuevoEstado,
            }));

            if (nuevoEstado === 'cerrado' && socket) {
                socket.emit('cerrarTicket', { identificador, token });
                const notificarCierre = await axios.post(`${BASE_URL}/notificar-cierre-cliente`, {
                    identificador,
                    usuario_creador: ticket.usuario_creador,
                    nombre_cliente: ticket.nombre,
                    asunto: ticket.asunto,
                }, {
                    headers: { Authorization: token }
                });
            }
        } catch (error) {
            console.error('Error al actualizar el estado del ticket:', error);
        }
    };

    const actualizarTecnicoAsignado = async (usuarioEncargado) => {
        try {
            const response = await axios.put(`${BASE_URL}/actualizar-tecnico`, {
                identificador,
                usuario_encargado: usuarioEncargado
            }, {
                headers: { Authorization: token }
            });

            setTicket((prevTicket) => ({
                ...prevTicket,
                usuario_encargado: usuarioEncargado
            }));


            if (usuarioEncargado !== "sin_asignar") {
                const notificarResponse = await axios.post(`${BASE_URL}/notificar-tecnico`, {
                    identificador,
                    usuario_encargado: usuarioEncargado,
                    usuario_notificador: tokenInfo.usuario,
                    usuario_creador: ticket.usuario_creador,
                    nombre_cliente: ticket.nombre,
                    apellidos_cliente: ticket.apellidos,
                    email_cliente: ticket.email,
                    webs_del_cliente: ticket.webs_del_cliente,
                    asunto: ticket.asunto,
                    descripcion: ticket.descripcion
                }, {
                    headers: { Authorization: token }
                });

            } else {
            }

        } catch (error) {
            console.error('Error al asignar o notificar al técnico del ticket:', error.response ? error.response.data : error.message);
        }
    };



    const obtenerNombreTecnicoAsignado = () => {
        if (ticket.usuario_encargado === 'sin_asignar') return null;
        const tecnico = tecnicos.find(t => t.usuario === ticket.usuario_encargado);
        return tecnico
            ? `${tecnico.nombre} ${tecnico.apellidos} (@${tecnico.usuario})`
            : `@${ticket.usuario_encargado}`;
    };

    // Filtrar técnicos por búsqueda
    const tecnicosFiltrados = tecnicos.filter(t => {
        const searchLower = searchTecnico.toLowerCase();
        return (
            t.nombre.toLowerCase().includes(searchLower) ||
            t.apellidos.toLowerCase().includes(searchLower) ||
            t.usuario.toLowerCase().includes(searchLower)
        );
    });

    // Función para seleccionar técnico
    const seleccionarTecnico = (usuario) => {
        actualizarTecnicoAsignado(usuario);
        setShowTecnicoSelector(false);
        setSearchTecnico('');
    };

    // Función para agrupar mensajes por fecha
    const agruparMensajesPorFecha = (mensajes) => {
        const grupos = [];
        let fechaActual = null;

        mensajes.forEach((mensaje, posicion) => {
            const fechaMensaje = new Date(mensaje.fecha).toLocaleDateString(i18n.language, {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });

            if (fechaMensaje !== fechaActual) {
                grupos.push({ tipo: 'separador', fecha: fechaMensaje });
                fechaActual = fechaMensaje;
            }

            // `posicion` es el índice real en la conversación: lo usan el id del
            // mensaje (enlaces directos) y la navegación por coincidencias
            grupos.push({ tipo: 'mensaje', data: mensaje, posicion });
        });

        return grupos;
    };

    // Función para determinar si debe mostrar avatar (último del grupo)
    const deberMostrarAvatar = (mensaje, siguienteMensaje) => {
        if (!siguienteMensaje) return true;
        return mensaje.usuario !== siguienteMensaje.usuario;
    };

    // Identificador estable del mensaje: sirve para el DOM, los enlaces directos y React
    const idDeMensaje = (mensaje, index) => mensaje.id || mensaje._clientId || `idx-${index}`;

    const esPersonalDeSoporte = tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico';

    /**
     * Editar y eliminar queda reservado al personal de soporte sobre sus propios
     * mensajes, sin límite de tiempo. El cliente no modifica lo ya enviado.
     * El servidor repite esta comprobación con el rol del token.
     */
    const puedeEditarMensaje = (mensaje) => (
        esPersonalDeSoporte
        && Boolean(mensaje.id)
        && mensaje.usuario === tokenInfo.usuario
        && !mensaje.__esDescripcion
        && !mensaje.eliminado
        && !mensaje._isTemp
        && !mensaje._falloEnvio
    );

    /** Emite por socket esperando confirmación del servidor (o fallo/timeout). */
    const emitirConAck = (evento, payload, ms = 10000) => new Promise((resolve) => {
        if (!socket || !socket.connected) {
            resolve({ ok: false, error: 'Sin conexión con el servidor' });
            return;
        }

        socket.timeout(ms).emit(evento, { ...payload, token }, (errorDeTiempo, respuesta) => {
            if (errorDeTiempo) {
                resolve({ ok: false, error: 'El servidor no respondió a tiempo' });
                return;
            }
            resolve(respuesta || { ok: false, error: 'Respuesta vacía del servidor' });
        });
    });

    // Sin texto ni adjuntos no hay nada que enviar: el botón se muestra inactivo
    const sinContenido = useMemo(
        () => esHtmlVacio(content) && filesToUpload.length === 0,
        [content, filesToUpload.length]
    );

    const renderBotonEnviar = () => (
        <button
            type="button"
            className={`tk-send-btn ${isSending ? 'tk-send-btn--enviando' : ''}`}
            onClick={enviarMensaje}
            disabled={isSending || sinContenido}
            title={sinContenido ? t('ticket.escribeOAdjunta') : t('ticket.enviarAtajo')}
        >
            {isSending
                ? <span className="tk-send-spinner" aria-hidden="true" />
                : <FiSend />}
            <span>{isSending ? t('ticket.enviando') : t('ticket.enviar')}</span>
        </button>
    );

    /** Sustituye un mensaje del chat localizándolo por su marca temporal. */
    const reemplazarMensaje = (clientId, cambios) => {
        setTicket((prevTicket) => ({
            ...prevTicket,
            chat: prevTicket.chat.map((m) => (
                m._clientId === clientId ? { ...m, ...cambios } : m
            )),
        }));
    };

    /**
     * Envía el mensaje y espera el ack del servidor: hasta que llega, el mensaje
     * aparece como pendiente; si falla, queda marcado como no entregado con la
     * opción de reintentar (los adjuntos ya subidos se reutilizan).
     */
    const emitirMensaje = async (payload, clientId) => {
        const respuesta = await emitirConAck('mensaje', { ...payload, clientId });

        if (respuesta.ok && respuesta.mensaje) {
            reemplazarMensaje(clientId, { ...respuesta.mensaje, _isTemp: false, _falloEnvio: false, _clientId: clientId });
            return true;
        }

        reemplazarMensaje(clientId, { _isTemp: false, _falloEnvio: true, _payload: payload });
        toast.error(respuesta.error || t('ticket.noSePudoEnviar'), {
            position: 'bottom-right',
            autoClose: 5000,
        });
        return false;
    };

    const enviarMensaje = async () => {
        // Evitamos que se envíe si ya se está procesando un envío
        if (isSending) return;

        if (sinContenido) {
            setErrorAlEnviar(true);
            return;
        }

        setIsSending(true);

        try {
            let uploadedFiles = [];

            if (filesToUpload.length > 0) {
                const formData = new FormData();
                filesToUpload.forEach(file => formData.append('archivoschat', file));

                const response = await axios.post(`${BASE_URL}/subir-archivos-chat/${identificador}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: token
                    },
                });

                uploadedFiles = response.data.archivosNuevos;
            }

            // Saneamos el HTML del editor antes de guardarlo: el chat lo pinta con innerHTML
            const processedContent = sanitizarHtml(processQuotes(content));
            const esNota = notaInterna && esPersonalDeSoporte;

            // IMPORTANTE: Usar identificador de useParams, NO del estado ticket
            const mensajeData = {
                identificador: identificador,
                mensaje: processedContent,
                usuario: tokenInfo.usuario,
                nombre: tokenInfo.nombre,
                apellidos: tokenInfo.apellidos,
                role: tokenInfo.role,
                fotoPerfil: tokenInfo.fotoPerfil,
                archivos: uploadedFiles,
                esNotaInterna: esNota,
                respuestaA: respondiendoA,
            };

            const clientId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

            // Actualización optimista: el mensaje se pinta como pendiente hasta el ack
            const mensajeOptimista = {
                usuario: mensajeData.usuario,
                nombre: mensajeData.nombre,
                apellidos: mensajeData.apellidos,
                role: mensajeData.role,
                fotoPerfil: mensajeData.fotoPerfil,
                texto: processedContent,
                fecha: new Date().toISOString(),
                archivos: uploadedFiles,
                esNotaInterna: esNota,
                respuestaA: respondiendoA,
                _isTemp: true,
                _clientId: clientId,
            };

            setAnimatingMessages(prev => [...prev, clientId]);
            setTimeout(() => {
                setAnimatingMessages(prev => prev.filter(id => id !== clientId));
            }, 500);

            setTicket((prevTicket) => ({
                ...prevTicket,
                chat: [...prevTicket.chat, mensajeOptimista],
                // Una nota interna no altera el estado de la conversación con el cliente
                estado_del_chat: esNota
                    ? prevTicket.estado_del_chat
                    : (esPersonalDeSoporte ? 'respondido' : 'sin_responder'),
            }));

            setErrorAlEnviar(false);
            setContent('');
            setSelectedFiles([]);
            setFilesToUpload([]);
            setNotaInterna(false);
            setRespondiendoA(null);

            await emitirMensaje(mensajeData, clientId);
        } catch (error) {
            console.error('Error al enviar el mensaje:', error);
            // No se limpia el editor: el mensaje sigue ahí para reintentar
            toast.error(t('ticket.noSePudieronSubirArchivos'), {
                position: 'bottom-right',
                autoClose: 5000,
            });
        } finally {
            // El botón vuelve a estar disponible en cuanto termina el envío real
            setIsSending(false);
        }
    };

    const reintentarEnvio = async (mensaje) => {
        if (!mensaje._payload || !mensaje._clientId) return;
        reemplazarMensaje(mensaje._clientId, { _isTemp: true, _falloEnvio: false });
        await emitirMensaje(mensaje._payload, mensaje._clientId);
    };

    // ── Acciones sobre un mensaje ────────────────────────────────────────────
    const copiarTextoMensaje = async (mensaje) => {
        try {
            // Solo lo que dice el mensaje: la cita a la que responde no se copia
            await navigator.clipboard.writeText(textoParaCopiar(mensaje.texto));
            toast.success(t('ticket.mensajeCopiado'), { position: 'bottom-right', autoClose: 2000 });
        } catch (error) {
            toast.error(t('ticket.noSePudoCopiarMensaje'), { position: 'bottom-right' });
        }
    };

    const copiarEnlaceMensaje = async (idMensaje) => {
        try {
            const enlace = `${window.location.href.split('#')[0]}#msg-${idMensaje}`;
            await navigator.clipboard.writeText(enlace);
            toast.success(t('ticket.enlaceCopiado'), { position: 'bottom-right', autoClose: 2000 });
        } catch (error) {
            toast.error(t('ticket.noSePudoCopiarEnlace'), { position: 'bottom-right' });
        }
    };

    /** Prepara la respuesta: la cita se muestra sobre el editor, no dentro del texto. */
    const responderAMensaje = (mensaje, idMensaje) => {
        const autor = `${mensaje.nombre || mensaje.usuario || ''} ${mensaje.apellidos || ''}`.trim();
        const texto = htmlATexto(mensaje.texto).trim();
        const extracto = texto.length > 160 ? `${texto.slice(0, 160)}…` : texto;

        setRespondiendoA({
            id: idMensaje,
            usuario: mensaje.usuario,
            autor: autor || mensaje.usuario,
            extracto: extracto || (mensaje.archivos?.length ? t('ticket.archivoAdjunto') : ''),
        });
    };

    /** Lleva la conversación hasta un mensaje y lo resalta un instante. */
    const irAMensaje = (idMensaje) => {
        const nodo = document.getElementById(`msg-${idMensaje}`);
        if (!nodo) {
            toast.info(t('ticket.mensajeYaNoDisponible'), { position: 'bottom-right' });
            return;
        }

        nodo.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setMensajeDestacado(idMensaje);
        setTimeout(() => setMensajeDestacado(null), 2500);
    };

    const guardarEdicionMensaje = async (idMensaje, texto) => {
        const limpio = sanitizarHtml(processQuotes(texto));
        if (esHtmlVacio(limpio)) return;

        const respuesta = await emitirConAck('editarMensaje', { identificador, id: idMensaje, texto: limpio });
        if (!respuesta.ok) {
            toast.error(respuesta.error || t('ticket.noSePudoEditar'), { position: 'bottom-right' });
            return;
        }
        setMensajeEnEdicion(null);
    };

    const eliminarMensaje = async (idMensaje) => {
        if (!window.confirm(t('ticket.confirmarEliminar'))) return;

        const respuesta = await emitirConAck('eliminarMensaje', { identificador, id: idMensaje });
        if (!respuesta.ok) {
            toast.error(respuesta.error || t('ticket.noSePudoEliminar'), { position: 'bottom-right' });
        }
    };

    const abrirArchivo = (archivo) => {
        setFilePreview({ url: archivo, name: getFileName(archivo, 120) });
    };

    useEffect(() => {
        const newSocket = io(`${BASE_URL}`);
        setSocket(newSocket);

        newSocket.on('ticketCerrado', (data) => {
            setTicket((prevTicket) => ({
                ...prevTicket,
                estado_del_ticket: 'cerrado'
            }));
        });

        return () => {
            newSocket.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!socket || !identificador) return undefined;

        // El token permite al servidor saber el rol y filtrar las notas internas
        const unirseALaSala = () => socket.emit('joinRoom', {
            identificador: identificador,
            usuario: tokenInfo.usuario,
            token,
        });

        unirseALaSala();
        // Tras una reconexión el servidor ya no recuerda ni la sala ni el rol
        socket.on('connect', unirseALaSala);

        // Cleanup: salir de la sala cuando el componente se desmonte o cambie el identificador
        return () => {
            socket.off('connect', unirseALaSala);
            socket.emit('leaveRoom', {
                identificador: identificador,
                usuario: tokenInfo.usuario
            });
        };
    }, [identificador, socket]);

    useEffect(() => {
        if (socket) {
            // Limpiar listeners anteriores para evitar duplicados
            socket.off('mensaje');
            
            socket.on('mensaje', (mensaje) => {

                setTicket((prevTicket) => {
                    // El eco del propio mensaje ya lo resolvió el ack: se descarta
                    const yaEsta = prevTicket.chat.some((m) => (
                        (m.id && mensaje.id && m.id === mensaje.id)
                        || (m._isTemp && m.usuario === mensaje.usuario && (m.texto || '') === (mensaje.texto || ''))
                    ));

                    if (yaEsta) {
                        return prevTicket;
                    }

                    const chatSinTemp = prevTicket.chat;
                    let nuevoEstadoChat = prevTicket.estado_del_chat;

                    // Las notas internas no cuentan como respuesta al cliente
                    if (mensaje.esNotaInterna) {
                        nuevoEstadoChat = prevTicket.estado_del_chat;
                    } else if (mensaje.role === 'user') {
                        nuevoEstadoChat = 'sin_responder';
                    } else if (mensaje.role === 'admin' || mensaje.role === 'tecnico') {
                        nuevoEstadoChat = 'respondido';
                    }

                    // Si el técnico asignado cambia (opcional, solo si lo necesitas)
                    const nuevoUsuarioEncargado = mensaje.usuario_encargado || prevTicket.usuario_encargado;

                    // Si el ticket estaba cerrado y el mensaje viene de un user, reabrirlo en local
                    const nuevoEstadoTicket =
                        prevTicket.estado_del_ticket === 'cerrado' && mensaje.role === 'user'
                            ? 'abierto'
                            : prevTicket.estado_del_ticket;

                    // Normalizar el mensaje para asegurar que tenga el formato correcto
                    const mensajeNormalizado = {
                        ...mensaje,
                        texto: mensaje.texto || mensaje.mensaje, // Asegurar que tenga 'texto'
                        fecha: mensaje.fecha || mensaje.createdAt || new Date().toISOString() // Asegurar que tenga 'fecha'
                    };

                    return {
                        ...prevTicket,
                        chat: [...chatSinTemp, mensajeNormalizado],
                        estado_del_chat: nuevoEstadoChat,
                        estado_del_ticket: nuevoEstadoTicket,
                        usuario_encargado: nuevoUsuarioEncargado
                    };
                });

                // Si estamos leyendo mensajes antiguos, avisamos en vez de saltar al final
                if (mensaje.usuario !== tokenInfo.usuario) {
                    setMensajesSinVer((n) => (lejosDelFinalRef.current ? n + 1 : 0));
                }
            });

            socket.off('mensajeEditado');
            socket.on('mensajeEditado', ({ mensaje }) => {
                setTicket((prevTicket) => ({
                    ...prevTicket,
                    chat: prevTicket.chat.map((m) => (m.id === mensaje.id ? { ...m, ...mensaje } : m)),
                }));
            });

            socket.off('mensajeEliminado');
            socket.on('mensajeEliminado', ({ mensaje }) => {
                setTicket((prevTicket) => ({
                    ...prevTicket,
                    chat: prevTicket.chat.map((m) => (m.id === mensaje.id ? { ...m, ...mensaje } : m)),
                }));
            });

            socket.off('mensajesLeidos');
            socket.on('mensajesLeidos', ({ ids }) => {
                setTicket((prevTicket) => ({
                    ...prevTicket,
                    chat: prevTicket.chat.map((m) => (ids.includes(m.id) ? { ...m, leido: true } : m)),
                }));
            });

            let typingTimeout;
            socket.on('escribiendo', (data) => {
                if (data.usuario !== tokenInfo.usuario) {
                    setUsuarioEscribiendo(data.nombre);
                    clearTimeout(typingTimeout);
                    typingTimeout = setTimeout(() => {
                        setUsuarioEscribiendo(null);
                    }, 3000);
                }
            });
        }

        return () => {
            if (socket) {
                socket.off('mensaje');
                socket.off('mensajeEditado');
                socket.off('mensajeEliminado');
                socket.off('mensajesLeidos');
                socket.off('escribiendo');
            }
        };
    }, [socket]);

    // ── Lectura, divisoria de nuevos y salto al final ────────────────────────
    // Los mensajes sin leer al entrar se congelan: la divisoria no debe evaporarse
    // en cuanto el servidor los marca como leídos.
    useEffect(() => {
        // Solo la primera vez tras cargar el ticket: después, los mensajes que
        // llegan en vivo se leen al momento y no deben mover la divisoria
        if (!permisosVerificados || noLeidosCalculadosRef.current) return;
        noLeidosCalculadosRef.current = true;

        setIdsNoLeidos(
            (ticket.chat || [])
                .filter((m) => m.id && !m.leido && m.usuario !== tokenInfo.usuario)
                .map((m) => m.id)
        );
    }, [permisosVerificados, ticket.chat, tokenInfo.usuario]);

    // Avisar al servidor de que hemos leído lo que hay (solo con la pestaña visible)
    useEffect(() => {
        if (!socket || !identificador) return;

        const marcarLeido = () => {
            if (document.visibilityState !== 'visible') return;
            const hayPendientes = (ticket.chat || []).some((m) => m.id && !m.leido && m.usuario !== tokenInfo.usuario);
            if (hayPendientes) socket.emit('marcarLeido', { identificador, token });
        };

        marcarLeido();
        document.addEventListener('visibilitychange', marcarLeido);
        return () => document.removeEventListener('visibilitychange', marcarLeido);
    }, [socket, identificador, ticket.chat, tokenInfo.usuario, token]);

    // Posición del scroll: con column-reverse el final es 0 (Chrome usa negativos al subir)
    useEffect(() => {
        const feed = chatRef.current;
        if (!feed) return undefined;

        const alDesplazar = () => {
            const lejos = Math.abs(feed.scrollTop) > 300;
            lejosDelFinalRef.current = lejos;
            setLejosDelFinal(lejos);
            if (!lejos) setMensajesSinVer(0);
        };

        feed.addEventListener('scroll', alDesplazar, { passive: true });
        return () => feed.removeEventListener('scroll', alDesplazar);
    }, [permisosVerificados]);

    const bajarAlFinal = () => {
        setMensajesSinVer(0);
        scrollToBottom(false);
    };

    // ── Enlace directo a un mensaje (#msg-...) ───────────────────────────────
    useEffect(() => {
        if (!permisosVerificados) return;

        const hash = window.location.hash;
        if (!hash.startsWith('#msg-')) return;

        const idMensaje = hash.replace('#msg-', '');
        const nodo = document.getElementById(`msg-${idMensaje}`);
        if (!nodo) return;

        nodo.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setMensajeDestacado(idMensaje);
        const temporizador = setTimeout(() => setMensajeDestacado(null), 3000);
        return () => clearTimeout(temporizador);
    }, [permisosVerificados, ticket.chat.length]);

    // ── Búsqueda en la conversación ──────────────────────────────────────────
    const coincidencias = useMemo(() => {
        if (!busqueda.trim()) return [];
        return mensajesConDescripcion
            .map((mensaje, index) => ({ id: idDeMensaje(mensaje, index), mensaje }))
            .filter(({ mensaje }) => !mensaje.eliminado && contieneTermino(mensaje.texto, busqueda))
            .map(({ id }) => id);
    }, [busqueda, mensajesConDescripcion]);

    const irACoincidencia = (siguiente) => {
        if (coincidencias.length === 0) return;

        const total = coincidencias.length;
        const nuevoIndice = ((siguiente ? indiceCoincidencia + 1 : indiceCoincidencia - 1) + total) % total;
        setIndiceCoincidencia(nuevoIndice);

        const nodo = document.getElementById(`msg-${coincidencias[nuevoIndice]}`);
        if (nodo) nodo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const cerrarBusqueda = () => {
        setBusqueda('');
        setBusquedaAbierta(false);
        setIndiceCoincidencia(0);
    };

    // Al cambiar el término, la navegación vuelve a empezar por la primera coincidencia
    useEffect(() => {
        setIndiceCoincidencia(0);
    }, [busqueda]);

    if (!permisosVerificados) {
        return <div className="loader-fullscreen"><Loader /></div>;
    }

    if (tokenInfo.estado === 'verificar') return <Verificacion />;
    if (tokenInfo.estado === 'suspendido') return <Suspendido />;

    if (permisoDenegado) {
        return <PermisoDenegado />;
    }

    return (
        <main className='tk-page site-width'>
            {ticket && (
                <>
                <div className='tk-header'>
                    <div className="tk-header-left">
                        {tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico' ? (
                        <button className="boton-volver" onClick={() => navigate('/dashboard/tickets')}>
                            <FiArrowLeft />
                        </button>
                        ) : (
                        <button className="boton-volver" onClick={() => navigate('/dashboard')}>
                            <FiArrowLeft />
                        </button>
                        )}
                        <div className="tk-header-title">
                            <h1>
                                <span className={`tk-dot tk-dot--${ticket.estado_del_ticket}`}></span>
                                {t('ticket.numero', { id: ticket.identificador })}
                            </h1>
                            <p>{ticket.asunto}</p>
                        </div>
                    </div>
                    <div className="tk-header-right">
                        <div className="tk-tecnico">
                            {ticket.usuario_encargado === "sin_asignar" ? <FiUserX className="tk-tecnico-icon tk-tecnico-icon--vacio" /> : <FiUserCheck className="tk-tecnico-icon tk-tecnico-icon--activo" />}
                            {tokenInfo.role === "admin" || tokenInfo.role === "tecnico" ? (
                                <div className="tk-tecnico-select">
                                    <button
                                        type="button"
                                        className="tk-tecnico-trigger"
                                        onClick={() => setShowTecnicoSelector(!showTecnicoSelector)}
                                        aria-haspopup="listbox"
                                        aria-expanded={showTecnicoSelector}
                                    >
                                        <span className="tk-tecnico-trigger-label">{t('ticket.tecnico')}</span>
                                        <span className="tk-tecnico-trigger-valor">
                                            {ticket.usuario_encargado === "sin_asignar"
                                                ? t('ticket.sinAsignar')
                                                : obtenerNombreTecnicoAsignado()}
                                        </span>
                                        <FiChevronDown className={`tk-tecnico-chevron ${showTecnicoSelector ? 'tk-tecnico-chevron--abierto' : ''}`} />
                                    </button>

                                    {showTecnicoSelector && (
                                        <div className="tk-tecnico-panel" ref={tecnicoSelectorRef}>
                                            <div className="tk-tecnico-panel-head">
                                                <h4>{t('ticket.asignarTecnico')}</h4>
                                                <span>{t('ticket.disponibles', { count: tecnicosFiltrados.length })}</span>
                                            </div>
                                            <div className="tk-tecnico-search">
                                                <FiSearch className="tk-tecnico-search-icon" />
                                                <input
                                                    type="text"
                                                    placeholder={t('ticket.buscarTecnico')}
                                                    value={searchTecnico}
                                                    onChange={(e) => setSearchTecnico(e.target.value)}
                                                    autoFocus
                                                />
                                                {searchTecnico !== '' && (
                                                    <button
                                                        type="button"
                                                        className="tk-tecnico-search-clear"
                                                        onClick={() => setSearchTecnico('')}
                                                        aria-label={t('ticket.limpiarBusqueda')}
                                                    >
                                                        <FiX />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="tk-tecnico-list" role="listbox">
                                                <button
                                                    type="button"
                                                    role="option"
                                                    aria-selected={ticket.usuario_encargado === "sin_asignar"}
                                                    className={`tk-tecnico-item ${ticket.usuario_encargado === "sin_asignar" ? 'tk-tecnico-item--activo' : ''}`}
                                                    onClick={() => seleccionarTecnico("sin_asignar")}
                                                >
                                                    <span className="tk-tecnico-item-avatar tk-tecnico-item-avatar--vacio">
                                                        <FiUserX />
                                                    </span>
                                                    <div className="tk-tecnico-item-info">
                                                        <span className="tk-tecnico-item-nombre">{t('ticket.sinAsignar')}</span>
                                                        <span className="tk-tecnico-item-usuario">{t('ticket.dejarSinResponsable')}</span>
                                                    </div>
                                                    {ticket.usuario_encargado === "sin_asignar" && <FiCheck className="tk-tecnico-item-check" />}
                                                </button>
                                                {tecnicosFiltrados.map((tecnico) => {
                                                    const activo = ticket.usuario_encargado === tecnico.usuario;
                                                    const iniciales = `${tecnico.nombre?.[0] || ''}${tecnico.apellidos?.[0] || ''}`.toUpperCase();
                                                    return (
                                                        <button
                                                            type="button"
                                                            role="option"
                                                            aria-selected={activo}
                                                            key={tecnico.usuario}
                                                            className={`tk-tecnico-item ${activo ? 'tk-tecnico-item--activo' : ''}`}
                                                            onClick={() => seleccionarTecnico(tecnico.usuario)}
                                                        >
                                                            <span className="tk-tecnico-item-avatar">{iniciales || '@'}</span>
                                                            <div className="tk-tecnico-item-info">
                                                                <span className="tk-tecnico-item-nombre">{tecnico.nombre} {tecnico.apellidos}</span>
                                                                <span className="tk-tecnico-item-usuario">@{tecnico.usuario}</span>
                                                            </div>
                                                            {activo && <FiCheck className="tk-tecnico-item-check" />}
                                                        </button>
                                                    );
                                                })}
                                                {tecnicosFiltrados.length === 0 && (
                                                    <div className="tk-tecnico-item tk-tecnico-item--vacio">
                                                        {t('ticket.sinTecnicosEncontrados')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <span className="tk-tecnico-texto">
                                    {ticket.usuario_encargado === "sin_asignar"
                                        ? t('ticket.sinAsignar')
                                        : obtenerNombreTecnicoAsignado()}
                                </span>
                            )}
                        </div>
                        <div className={`tk-status-pill ${ticket.estado_del_chat === "respondido" ? 'tk-status-pill--ok' : ''}`}>
                            <span className="tk-status-dot"></span>
                            <p>
                                {ticket.estado_del_chat === "sin_responder" ? t('ticket.esperandoRespuesta') : ''}
                                {ticket.estado_del_chat === "respondido" ? t('ticket.respondido') : ''}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="tk-layout">
                    {datosDrawerAbierto && (
                        <div className="tk-drawer-backdrop" onClick={() => setDatosDrawerAbierto(false)}></div>
                    )}
                    <aside
                        className={`tk-drawer ${datosDrawerAbierto ? 'tk-drawer--abierto' : ''}`}
                        aria-hidden={!datosDrawerAbierto}
                    >
                        {/* Cabecera fija: identifica el panel y deja el cierre siempre
                            a mano aunque se haya bajado en el scroll. */}
                        <div className="tk-drawer-topbar">
                            <h2><FiInfo /> {t('ticket.datosDelTicket')}</h2>
                            <button
                                type="button"
                                className="tk-drawer-close"
                                onClick={() => setDatosDrawerAbierto(false)}
                                aria-label={t('ticket.cerrarDatosTicket')}
                            >
                                <FiX />
                            </button>
                        </div>
                        {showAllFiles ? (
                            <div className="tk-drawer-page">
                                <button type="button" className="tk-drawer-back" onClick={() => setShowAllFiles(false)}>
                                    <FiArrowLeft /> {t('ticket.volverADatos')}
                                </button>
                                <h3 className="tk-drawer-page-title"><FiFolder /> {t('ticket.archivosIncluidos')}</h3>
                                <div className='tk-files-modal-grid'>
                                    {todosLosArchivos.map((file, index) => (
                                        <button type="button" onClick={() => abrirArchivo(file)} key={index} className='tk-file-chip'>
                                            <img src={getIconUrl(file)} alt={t('ticket.archivo', { nombre: getFileName(file) })} />
                                            <p>{getFileName(file)}</p>
                                            <span className='tk-file-download'><FiDownload /></span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="tk-drawer-card">
                                    <div className="tk-drawer-summary">
                                        <h3>{ticket.asunto}</h3>
                                        <div className='tk-drawer-summary-meta'>
                                            <span><FiHash /> {ticket.identificador}</span>
                                            <span><FiClock /> {new Date(ticket.createdAt).toLocaleString(i18n.language, {
                                                day: '2-digit',
                                                month: 'long',
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: false
                                            })}</span>
                                        </div>
                                    </div>
                                    <div className="tk-drawer-grid">
                                        <div className="tk-field">
                                            <span className="tk-field-label"><FiUser /> {t('ticket.nombreCompleto')}</span>
                                            <p>{ticket.nombre} {ticket.apellidos}</p>
                                        </div>
                                        <div className="tk-field">
                                            <span className="tk-field-label"><FiMail /> {t('comun.email')}</span>
                                            {/* Datos de contacto accionables: escribir o llamar sin copiar a mano */}
                                            <p><a href={`mailto:${ticket.email}`}>{ticket.email}</a></p>
                                        </div>
                                        <div className="tk-field">
                                            <span className="tk-field-label"><FiPhone /> {t('comun.telefono')}</span>
                                            <p><a href={`tel:${(ticket.telefono || '').replace(/\s+/g, '')}`}>{ticket.telefono}</a></p>
                                        </div>
                                        <div className="tk-field">
                                            <span className="tk-field-label"><FiGlobe /> {t('ticket.websMantenimiento')}</span>
                                            <p>{ticket.webs_del_cliente}</p>
                                        </div>
                                        <div className="tk-field">
                                            <span className="tk-field-label"><FiGlobe /> {t('ticket.urlSolicitud')}</span>
                                            <p>
                                                {ticket.url_solicitud ? (
                                                    <a href={ticket.url_solicitud} target="_blank" rel="noopener noreferrer">
                                                        {ticket.url_solicitud}
                                                    </a>
                                                ) : t('ticket.urlNoAplica')}
                                            </p>
                                        </div>
                                    </div>
                                    <hr />
                                    <div className="tk-drawer-section">
                                        <h3><FiSettings /> {t('ticket.gestionTicket')}</h3>
                                        <p>{t('ticket.gestionTicketTexto')}</p>
                                        <div className="tk-manage-grid">
                                            <div className="tk-field">
                                                <span className="tk-field-label">{t('ticket.departamento')}</span>
                                                {tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico' ? (
                                                    <TicketSelect options={opcionesDpto} value={ticket.dpto} onChange={actualizarDpto} />
                                                ) : ticket.dpto ? (
                                                    <p className={ticket.dpto.replace('_', '-')}>{t(`tickets.departamentos.${ticket.dpto}`)}</p>
                                                ) : (
                                                    ''
                                                )}
                                            </div>
                                            <div className="tk-field">
                                                <span className="tk-field-label">{t('ticket.prioridad')}</span>
                                                {tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico' ? (
                                                    <TicketSelect options={opcionesPrioridad} value={ticket.prioridad} onChange={actualizarPrioridad} />
                                                ) : ticket.prioridad ? (
                                                    <p className={ticket.prioridad === 'urgente' ? 'alta' : 'sin-prioridad'}>{t(`tickets.prioridades.${ticket.prioridad}`)}</p>
                                                ) : (
                                                    ''
                                                )}
                                            </div>
                                            <div className="tk-field">
                                                <span className="tk-field-label"><FiClock /> {t('ticket.fechaLimite')}</span>
                                                <p className={fechaLimiteVencida ? 'tk-fecha-limite-vencida' : ''}>
                                                    {formatearFechaLimite(ticket.fecha_limite) || '—'}
                                                </p>
                                            </div>
                                            <div className="tk-field">
                                                <span className="tk-field-label">{t('ticket.estado')}</span>
                                                {tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico' ? (
                                                    <TicketSelect options={opcionesEstado} value={ticket.estado_del_ticket} onChange={actualizarEstadoDelTicket} />
                                                ) : ticket.estado_del_ticket ? (
                                                    <p className={ticket.estado_del_ticket}>{t(`tickets.estados.${ticket.estado_del_ticket}`)}</p>
                                                ) : (
                                                    ''
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {tokenInfo.role === "admin" && (
                                        <>
                                            <hr />
                                            <div className="tk-admin-actions">
                                                <h3>{t('ticket.accionesAdmin')}</h3>
                                                <p>{t('ticket.soloAdmins')}</p>
                                                <button onClick={copiarTicket} className="tk-copy-btn">
                                                    <FiCopy />
                                                    <p>{t('ticket.copiarInfo')}</p>
                                                </button>
                                                <button onClick={exportarTicketPdf} className="tk-copy-btn tk-copy-btn--secundario" disabled={exportandoPdf}>
                                                    <FiFileText />
                                                    <p>{exportandoPdf ? t('ticket.exportandoPdf') : t('ticket.exportarPdf')}</p>
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className='tk-files-card'>
                                    <p>
                                        <FiFolder /> {t('ticket.archivosAportados')}
                                        {todosLosArchivos.length > 0 && (
                                            <span className='tk-files-count'>{todosLosArchivos.length}</span>
                                        )}
                                    </p>
                                    {todosLosArchivos.length > 0 ? (
                                        <>
                                            <div className='tk-files-grid'>
                                                {todosLosArchivos
                                                    .slice(0, 3)
                                                    .map((file, index) => (
                                                        <button type="button" onClick={() => abrirArchivo(file)} key={index} className='tk-file-chip'>
                                                            <img src={getIconUrl(file)} alt={t('ticket.archivo', { nombre: getFileName(file) })} />
                                                            <p>{getFileName(file)}</p>
                                                            <span className='tk-file-download'><FiDownload /></span>
                                                        </button>
                                                    ))}
                                            </div>
                                            {todosLosArchivos.length > 3 && (
                                                <button className='tk-ver-todos' onClick={() => setShowAllFiles(true)}>
                                                    <FiFolder /> {t('ticket.verArchivos', { count: todosLosArchivos.length })}
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        <p className='tk-files-empty'>{t('ticket.sinArchivos')}</p>
                                    )}
                                </div>
                            </>
                        )}
                    </aside>
                    <section className="tk-chat" ref={chatTicketRef}>
                        <div className="tk-chat-header">
                            <div className="tk-chat-title">
                                <span className="tk-chat-count">
                                    {t('ticket.mensajes', { count: mensajesConDescripcion.length })}
                                </span>
                            </div>
                            <div className="tk-chat-header-acciones">
                                {busquedaAbierta ? (
                                    <div className="tk-buscador">
                                        <FiSearch />
                                        <input
                                            type="text"
                                            value={busqueda}
                                            autoFocus
                                            placeholder={t('ticket.buscarConversacion')}
                                            onChange={(e) => setBusqueda(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') { e.preventDefault(); irACoincidencia(!e.shiftKey); }
                                                if (e.key === 'Escape') cerrarBusqueda();
                                            }}
                                        />
                                        {busqueda.trim() !== '' && (
                                            <span className="tk-buscador-contador">
                                                {coincidencias.length === 0 ? t('ticket.sinResultados') : `${indiceCoincidencia + 1}/${coincidencias.length}`}
                                            </span>
                                        )}
                                        <button type="button" title={t('ticket.coincidenciaAnterior')} aria-label={t('ticket.coincidenciaAnterior')} disabled={coincidencias.length === 0} onClick={() => irACoincidencia(false)}>
                                            <FiChevronUp />
                                        </button>
                                        <button type="button" title={t('ticket.coincidenciaSiguiente')} aria-label={t('ticket.coincidenciaSiguiente')} disabled={coincidencias.length === 0} onClick={() => irACoincidencia(true)}>
                                            <FiChevronDown />
                                        </button>
                                        <button type="button" title={t('ticket.cerrarBusqueda')} aria-label={t('ticket.cerrarBusqueda')} onClick={cerrarBusqueda}>
                                            <FiX />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        className="tk-chat-header-btn"
                                        onClick={() => setBusquedaAbierta(true)}
                                        title={t('ticket.buscarConversacion')}
                                    >
                                        <FiSearch />
                                        <span>{t('ticket.buscar')}</span>
                                    </button>
                                )}
                                <button
                                    className={`tk-chat-header-btn ${datosDrawerAbierto ? 'tk-chat-header-btn--activo' : ''}`}
                                    onClick={() => setDatosDrawerAbierto(!datosDrawerAbierto)}
                                    title={datosDrawerAbierto ? t('ticket.ocultarDatosTicket') : t('ticket.verDatosTicket')}
                                >
                                    <FiInfo />
                                    <span>{t('ticket.datosDelTicket')}</span>
                                </button>
                            </div>
                        </div>
                        <div className="tk-feed-wrap">
                        <div className="tk-feed" ref={chatRef}>
                            {agruparMensajesPorFecha(mensajesConDescripcion).reverse().map((item, index, array) => {
                                if (item.tipo === 'separador') {
                                    return (
                                        <div key={`sep-${index}`} className="tk-date-sep">
                                            <span>{item.fecha}</span>
                                        </div>
                                    );
                                }

                                const msg = item.data;
                                const posicion = item.posicion;
                                const idMensaje = idDeMensaje(msg, posicion);
                                const esMio = msg.usuario === tokenInfo.usuario;
                                const siguienteMensaje = array[index - 1]?.tipo === 'mensaje' ? array[index - 1].data : null;
                                const mostrarAvatar = deberMostrarAvatar(msg, siguienteMensaje);
                                const primerNoLeido = idsNoLeidos.length > 0 && idsNoLeidos[0] === msg.id;

                                return (
                                    <React.Fragment key={idMensaje}>
                                        <MensajeChat
                                            id={`msg-${idMensaje}`}
                                            mensaje={msg}
                                            esMio={esMio}
                                            mostrarAvatar={mostrarAvatar}
                                            esAnimado={animatingMessages.includes(msg._clientId)}
                                            puedeEditar={puedeEditarMensaje(msg)}
                                            terminoBusqueda={busqueda.trim()}
                                            esCoincidenciaActiva={coincidencias[indiceCoincidencia] === idMensaje}
                                            destacado={mensajeDestacado === idMensaje}
                                            enEdicion={mensajeEnEdicion === msg.id}
                                            dominioImagenes={IMAGE_DOMAIN}
                                            onIniciarEdicion={() => setMensajeEnEdicion(msg.id)}
                                            onCancelarEdicion={() => setMensajeEnEdicion(null)}
                                            onGuardarEdicion={(texto) => guardarEdicionMensaje(msg.id, texto)}
                                            onEliminar={() => eliminarMensaje(msg.id)}
                                            onResponder={() => responderAMensaje(msg, idMensaje)}
                                            onIrAMensaje={irAMensaje}
                                            onCopiarTexto={() => copiarTextoMensaje(msg)}
                                            onCopiarEnlace={() => copiarEnlaceMensaje(idMensaje)}
                                            onReintentar={() => reintentarEnvio(msg)}
                                            onAbrirArchivo={abrirArchivo}
                                        />
                                        {primerNoLeido && (
                                            <div className="tk-nuevos-sep"><span>{t('ticket.nuevosMensajes')}</span></div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            <div className='bottomRef' ref={bottomRef}></div>
                        </div>
                        {(lejosDelFinal || mensajesSinVer > 0) && (
                            <button className="tk-bajar" onClick={bajarAlFinal} title={t('ticket.irAlUltimo')}>
                                <FiArrowDown />
                                {mensajesSinVer > 0 && <span className="tk-bajar-contador">{mensajesSinVer}</span>}
                            </button>
                        )}
                        </div>
                        <div className="tk-composer">
                            {ticket.estado_del_ticket === 'cerrado' && (
                                <p className="tk-reopen-banner"><FiAlertCircle /> {t('ticket.ticketCerradoAviso')}</p>
                            )}
                            {usuarioEscribiendo && (
                                <div className="tk-typing">
                                    <span className="tk-typing-dots"><span></span><span></span><span></span></span>
                                    <p>{usuarioEscribiendo}</p>
                                </div>
                            )}
                            {tokenInfo.role === "admin" &&
                                ticket.usuario_encargado === "sin_asignar" ? (
                                <div className='tk-composer-bloqueado'>
                                    <span className="tk-composer-bloqueado-icono"><FiUserX /></span>
                                    <div className="tk-composer-bloqueado-texto">
                                        <p className="tk-composer-bloqueado-titulo">{t('ticket.chatNoHabilitado')}</p>
                                        <p>{t('ticket.asignaParaEmpezar')}</p>
                                    </div>
                                    <button
                                        type="button"
                                        className="tk-composer-bloqueado-btn"
                                        onClick={() => setShowTecnicoSelector(true)}
                                    >
                                        <FiUserCheck /> {t('ticket.asignarTecnico')}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {
                                        (tokenInfo.role === "admin" || tokenInfo.role === "tecnico") && (
                                            <div className="tk-quick-replies">
                                                <p><FiZap /> {t('ticket.mensajesPreconfigurados')}</p>
                                                <div className="tk-quick-replies-list">
                                                    {['bienvenida', 'procesando', 'incidenciaResuelta', 'tareaResuelta', 'escalar'].map((clave) => (
                                                        <button
                                                            key={clave}
                                                            onClick={() => setContent(
                                                                t(`ticket.respuestasRapidas.${clave}.texto`, {
                                                                    nombre: clave === 'incidenciaResuelta' || clave === 'tareaResuelta' ? ticket.nombre : ticket.nombre,
                                                                    firma: tokenInfo.nombre,
                                                                })
                                                            )}
                                                        >
                                                            {t(`ticket.respuestasRapidas.${clave}.boton`)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    }
                                    <div className="tk-uploader">
                                        {errorAlEnviar && (
                                            <p className='error tk-uploader-error'>{t('ticket.mensajeVacio')}</p>
                                        )}
                                        <div className="tk-file-list">
                                            {selectedFiles.map((file, index) => (
                                                <div key={index} className="tk-file-item">
                                                    <span>{file.name}</span>
                                                    <button className="tk-file-remove" onClick={() => handleRemoveFile(index)}><FiX /></button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={`tk-composer-editor ${notaInterna ? 'tk-composer-editor--nota' : ''} ${respondiendoA ? 'tk-composer-editor--respondiendo' : ''}`}>
                                        {respondiendoA && (
                                            <div className="tk-respuesta-barra">
                                                <span className="tk-respuesta-marca" aria-hidden="true" />
                                                <button
                                                    type="button"
                                                    className="tk-respuesta-info"
                                                    onClick={() => irAMensaje(respondiendoA.id)}
                                                    title={t('ticket.irAlMensajeOriginal')}
                                                >
                                                    <span className="tk-respuesta-autor">
                                                        <FiCornerUpLeft /> {t('ticket.respondiendoA', { quien: respondiendoA.usuario === tokenInfo.usuario ? t('ticket.ti') : respondiendoA.autor })}
                                                    </span>
                                                    <span className="tk-respuesta-extracto">{respondiendoA.extracto}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="tk-respuesta-cerrar"
                                                    onClick={() => setRespondiendoA(null)}
                                                    title={t('ticket.cancelarRespuesta')}
                                                    aria-label={t('ticket.cancelarRespuesta')}
                                                >
                                                    <FiX />
                                                </button>
                                            </div>
                                        )}
                                        <RichTextEditor
                                            value={content}
                                            onChange={handleChange}
                                            onFiles={processFiles}
                                            dropZoneRef={chatTicketRef}
                                            onSubmit={enviarMensaje}
                                            placeholder={notaInterna ? t('ticket.escribeNotaInterna') : t('ticket.escribeMensaje')}
                                        />
                                    </div>
                                    <div className="tk-composer-acciones">
                                        {renderBotonEnviar()}
                                        {esPersonalDeSoporte && (
                                            <button
                                                type="button"
                                                className={`tk-nota-toggle ${notaInterna ? 'tk-nota-toggle--activa' : ''}`}
                                                onClick={() => setNotaInterna((activa) => !activa)}
                                                aria-pressed={notaInterna}
                                                title={t('ticket.notasSoloSoporte')}
                                            >
                                                <FiLock />
                                                <span>{t('ticket.notaInterna')}</span>
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </div>
                </>
            )}
            <FilePreviewModal
                file={filePreview}
                onClose={() => setFilePreview(null)}
                onDownload={(file) => descargarArchivo(file.url)}
            />
            <ToastContainer position="bottom-right" autoClose={3000} />
        </main>
    );
};

export default Ticket;
